import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { getHelpArticleUrl, HELP_ARTICLE_EMAIL_CAMPAIGNS } from "../../../helpers/helpCenter";
import {
  createEmailCampaign,
  EmailCampaign,
  EmailCampaignPreview,
  EmailCampaignUsage,
  getEmailCampaignPreview,
  listEmailCampaigns,
  sendEmailCampaign,
} from "../../../services/emailCampaignService";

const DEFAULT_USAGE: EmailCampaignUsage = {
  transactional_count: 0,
  campaign_count: 0,
  campaign_quota: 0,
  limit: null,
  is_free: false,
  is_near_limit: false,
  is_at_limit: false,
  is_campaign_near_limit: false,
  is_campaign_at_limit: false,
};

const EMPTY_STATS = {
  sent: 0,
  bounced: 0,
  complained: 0,
  skipped_suppressed: 0,
  failed: 0,
  opted_out: 0,
};

const statusColor = (status: string): string => {
  switch (status) {
    case "sent":
      return "success";
    case "sending":
      return "info";
    case "scheduled":
      return "warning";
    case "canceled":
      return "secondary";
    default:
      return "primary";
  }
};

const toLocalInputValue = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const EmailCampaignsPage = () => {
  const { t, i18n } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [sendingId, setSendingId] = useState<number | null>(null);
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [usage, setUsage] = useState<EmailCampaignUsage>(DEFAULT_USAGE);
  const [eligibleCount, setEligibleCount] = useState(0);
  const [name, setName] = useState("");
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduledLocal, setScheduledLocal] = useState(() => {
    const d = new Date();
    d.setHours(d.getHours() + 2);
    return toLocalInputValue(d);
  });
  const [preview, setPreview] = useState<EmailCampaignPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"create" | "send-draft">("create");
  const [confirmCampaign, setConfirmCampaign] = useState<EmailCampaign | null>(null);
  const [reactivationEnabled, setReactivationEnabled] = useState(true);

  useEffect(() => {
    document.title = t("marketing.emailCampaigns.pageTitle");
  }, [t]);

  const loadPreview = useCallback(async () => {
    setLoadingPreview(true);
    try {
      const response = await getEmailCampaignPreview();
      setPreview(response.data);
    } catch {
      setPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);

    try {
      const response = await listEmailCampaigns();
      setCampaigns(response.data.campaigns?.data ?? []);
      setUsage(response.data.usage ?? DEFAULT_USAGE);
      setEligibleCount(response.data.estimate?.eligible_count ?? 0);
      setReactivationEnabled(
        response.data.reactivation_campaigns_enabled !== false
      );
    } catch (error: any) {
      toast.error(error?.data || t("marketing.emailCampaigns.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
    loadPreview();
  }, [load, loadPreview]);

  const helpUrl = useMemo(
    () => getHelpArticleUrl(HELP_ARTICLE_EMAIL_CAMPAIGNS, i18n.language),
    [i18n.language]
  );

  const openCreateConfirm = () => {
    if (!name.trim()) {
      toast.error(t("marketing.emailCampaigns.form.nameRequired"));
      return;
    }
    if (scheduleEnabled && !scheduledLocal) {
      toast.error(t("marketing.emailCampaigns.form.scheduleRequired"));
      return;
    }
    setConfirmMode("create");
    setConfirmCampaign(null);
    setConfirmOpen(true);
  };

  const openSendDraftConfirm = (campaign: EmailCampaign) => {
    setConfirmMode("send-draft");
    setConfirmCampaign(campaign);
    setConfirmOpen(true);
  };

  const toastApiError = (error: any) => {
    const code = error?.code as string | undefined;
    if (code === "EMAIL_CAMPAIGN_QUOTA_EXCEEDED") {
      toast.error(t("marketing.emailCampaigns.errors.quota"));
    } else if (code === "EMAIL_CIRCUIT_OPEN") {
      toast.error(t("marketing.emailCampaigns.errors.circuit"));
    } else {
      toast.error(error?.data || t("marketing.emailCampaigns.toast.sendError"));
    }
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);

    if (confirmMode === "send-draft" && confirmCampaign) {
      setSendingId(confirmCampaign.id);
      try {
        await sendEmailCampaign(confirmCampaign.id);
        toast.success(t("marketing.emailCampaigns.toast.sendSuccess"));
        await load();
      } catch (error: any) {
        toastApiError(error);
      } finally {
        setSendingId(null);
      }
      return;
    }

    const trimmed = name.trim();
    setCreating(true);

    try {
      const payload: {
        name: string;
        template_key: string;
        segment: { inactive_days: number };
        scheduled_at?: string;
      } = {
        name: trimmed,
        template_key: "inactive_clients_90d",
        segment: { inactive_days: 90 },
      };

      if (scheduleEnabled) {
        payload.scheduled_at = new Date(scheduledLocal).toISOString();
      }

      const created = await createEmailCampaign(payload);

      if (scheduleEnabled) {
        toast.success(t("marketing.emailCampaigns.toast.scheduleSuccess"));
        setName("");
        setScheduleEnabled(false);
        await load();
        return;
      }

      setSendingId(created.data.id);
      await sendEmailCampaign(created.data.id);
      toast.success(t("marketing.emailCampaigns.toast.sendSuccess"));
      setName("");
      await load();
    } catch (error: any) {
      toastApiError(error);
    } finally {
      setCreating(false);
      setSendingId(null);
    }
  };

  const quotaLabel = `${usage.campaign_count} / ${usage.campaign_quota}`;
  const campaignsDisabled = usage.campaign_quota <= 0 || usage.is_campaign_at_limit;
  const confirmCount =
    confirmMode === "send-draft"
      ? confirmCampaign?.recipients_count ?? eligibleCount
      : eligibleCount;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("marketing.emailCampaigns.breadcrumb")}
            pageTitle={t("marketing.emailCampaigns.pageTitle")}
          />

          <Row>
            <Col lg={12}>
              <Alert color="info" className="border-0 mb-2">
                {t("marketing.emailCampaigns.intro")}{" "}
                <Link to="/settings/email-notifications">
                  {t("marketing.emailCampaigns.linkSettings")}
                </Link>
                {" · "}
                <Link to="/marketing/contact-ruler">
                  {t("marketing.emailCampaigns.linkContactRuler")}
                </Link>
                {" · "}
                <a href={helpUrl} target="_blank" rel="noreferrer">
                  {t("marketing.emailCampaigns.linkHelp")}
                </a>
              </Alert>
              {!reactivationEnabled && (
                <Alert color="warning" className="border-0">
                  {t("marketing.emailCampaigns.reactivationOff")}{" "}
                  <Link to="/marketing/contact-ruler">
                    {t("marketing.emailCampaigns.reactivationOffCta")}
                  </Link>
                </Alert>
              )}
            </Col>
          </Row>

          <Row>
            <Col lg={4}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">{t("marketing.emailCampaigns.quota.title")}</h5>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <p className="mb-2 fs-4 fw-semibold">{quotaLabel}</p>
                      <p className="text-muted mb-0">
                        {t("marketing.emailCampaigns.quota.hint")}
                      </p>
                      {usage.campaign_quota <= 0 && (
                        <Alert color="warning" className="mt-3 mb-0">
                          {t("marketing.emailCampaigns.quota.freeUpsell")}
                        </Alert>
                      )}
                      {usage.is_campaign_near_limit && usage.campaign_quota > 0 && (
                        <Alert color="warning" className="mt-3 mb-0">
                          {t("marketing.emailCampaigns.quota.nearLimit")}
                        </Alert>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>

              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">{t("marketing.emailCampaigns.preview.title")}</h5>
                  <Button color="soft-secondary" size="sm" onClick={loadPreview} disabled={loadingPreview}>
                    {loadingPreview ? <Spinner size="sm" /> : t("marketing.emailCampaigns.preview.refresh")}
                  </Button>
                </CardHeader>
                <CardBody>
                  {preview ? (
                    <>
                      <p className="small text-muted mb-2">
                        <strong>{t("marketing.emailCampaigns.preview.subject")}:</strong> {preview.subject}
                      </p>
                      <div
                        className="border rounded bg-light p-2"
                        style={{ maxHeight: 280, overflow: "auto" }}
                        dangerouslySetInnerHTML={{ __html: preview.html }}
                      />
                    </>
                  ) : (
                    <p className="text-muted mb-0">{t("marketing.emailCampaigns.preview.empty")}</p>
                  )}
                </CardBody>
              </Card>
            </Col>

            <Col lg={8}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">{t("marketing.emailCampaigns.form.title")}</h5>
                </CardHeader>
                <CardBody>
                  <p className="text-muted">
                    {t("marketing.emailCampaigns.form.segmentHint", { count: eligibleCount })}
                  </p>

                  {eligibleCount === 0 && !loading && (
                    <Alert color="secondary">
                      {t("marketing.emailCampaigns.empty.noEligible")}
                    </Alert>
                  )}

                  <div className="mb-3">
                    <Label htmlFor="campaign-name">{t("marketing.emailCampaigns.form.name")}</Label>
                    <Input
                      id="campaign-name"
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      placeholder={t("marketing.emailCampaigns.form.namePlaceholder")}
                      disabled={campaignsDisabled || creating}
                      maxLength={120}
                    />
                  </div>

                  <FormGroup check className="mb-3">
                    <Input
                      id="schedule-enabled"
                      type="checkbox"
                      checked={scheduleEnabled}
                      onChange={(e) => setScheduleEnabled(e.target.checked)}
                      disabled={campaignsDisabled || creating}
                    />
                    <Label check htmlFor="schedule-enabled">
                      {t("marketing.emailCampaigns.form.scheduleToggle")}
                    </Label>
                  </FormGroup>

                  {scheduleEnabled && (
                    <div className="mb-3">
                      <Label htmlFor="scheduled-at">{t("marketing.emailCampaigns.form.scheduledAt")}</Label>
                      <Input
                        id="scheduled-at"
                        type="datetime-local"
                        value={scheduledLocal}
                        onChange={(e) => setScheduledLocal(e.target.value)}
                        disabled={campaignsDisabled || creating}
                      />
                      <small className="text-muted d-block mt-1">
                        {t("marketing.emailCampaigns.form.scheduleHint")}
                      </small>
                    </div>
                  )}

                  <Button
                    color="primary"
                    onClick={openCreateConfirm}
                    disabled={campaignsDisabled || creating || eligibleCount === 0}
                  >
                    {creating ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        {t("marketing.emailCampaigns.form.sending")}
                      </>
                    ) : scheduleEnabled ? (
                      t("marketing.emailCampaigns.form.scheduleSubmit")
                    ) : (
                      t("marketing.emailCampaigns.form.submit")
                    )}
                  </Button>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">{t("marketing.emailCampaigns.list.title")}</h5>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <div className="text-center py-4">
                      <Spinner />
                    </div>
                  ) : campaigns.length === 0 ? (
                    <Alert color="light" className="mb-0 border">
                      {t("marketing.emailCampaigns.empty.list")}
                    </Alert>
                  ) : (
                    <div className="table-responsive">
                      <Table className="align-middle mb-0">
                        <thead>
                          <tr>
                            <th>{t("marketing.emailCampaigns.list.name")}</th>
                            <th>{t("marketing.emailCampaigns.list.status")}</th>
                            <th>{t("marketing.emailCampaigns.list.recipients")}</th>
                            <th>{t("marketing.emailCampaigns.list.sent")}</th>
                            <th>{t("marketing.emailCampaigns.list.bounced")}</th>
                            <th>{t("marketing.emailCampaigns.list.failed")}</th>
                            <th>{t("marketing.emailCampaigns.list.sentAt")}</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {campaigns.map((campaign) => {
                            const stats = campaign.stats ?? EMPTY_STATS;
                            return (
                              <tr key={campaign.id}>
                                <td>
                                  <div>{campaign.name}</div>
                                  {campaign.scheduled_at && campaign.status === "scheduled" && (
                                    <small className="text-muted">
                                      {t("marketing.emailCampaigns.list.scheduledFor", {
                                        date: new Date(campaign.scheduled_at).toLocaleString(),
                                      })}
                                    </small>
                                  )}
                                </td>
                                <td>
                                  <Badge color={statusColor(campaign.status)} className="text-uppercase">
                                    {t(`marketing.emailCampaigns.status.${campaign.status}`)}
                                  </Badge>
                                </td>
                                <td>{campaign.recipients_count ?? "—"}</td>
                                <td>{stats.sent}</td>
                                <td>{stats.bounced}</td>
                                <td>{stats.failed}</td>
                                <td>
                                  {campaign.sent_at
                                    ? new Date(campaign.sent_at).toLocaleString()
                                    : "—"}
                                </td>
                                <td className="text-end">
                                  {(campaign.status === "draft" || campaign.status === "scheduled") && (
                                    <Button
                                      color="soft-primary"
                                      size="sm"
                                      disabled={sendingId === campaign.id || campaignsDisabled}
                                      onClick={() => openSendDraftConfirm(campaign)}
                                    >
                                      {sendingId === campaign.id ? (
                                        <Spinner size="sm" />
                                      ) : (
                                        t("marketing.emailCampaigns.list.send")
                                      )}
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  )}
                  <p className="text-muted small mt-3 mb-0">
                    {t("marketing.emailCampaigns.list.statsNote")}
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={confirmOpen} toggle={() => setConfirmOpen(false)} centered>
        <ModalHeader toggle={() => setConfirmOpen(false)}>
          {t("marketing.emailCampaigns.confirm.title")}
        </ModalHeader>
        <ModalBody>
          <p className="mb-2">
            {scheduleEnabled && confirmMode === "create"
              ? t("marketing.emailCampaigns.confirm.scheduleBody", {
                  count: confirmCount,
                  date: new Date(scheduledLocal).toLocaleString(),
                })
              : t("marketing.emailCampaigns.confirm.sendBody", { count: confirmCount })}
          </p>
          <Alert color="light" className="mb-0 border">
            {t("marketing.emailCampaigns.confirm.quotaHint", {
              used: usage.campaign_count,
              quota: usage.campaign_quota,
            })}
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setConfirmOpen(false)}>
            {t("marketing.emailCampaigns.confirm.cancel")}
          </Button>
          <Button color="primary" onClick={handleConfirm}>
            {scheduleEnabled && confirmMode === "create"
              ? t("marketing.emailCampaigns.confirm.confirmSchedule")
              : t("marketing.emailCampaigns.confirm.confirmSend")}
          </Button>
        </ModalFooter>
      </Modal>

      <ToastContainer />
    </React.Fragment>
  );
};

export default EmailCampaignsPage;
